import {Page} from "../../../component/Page";
import React, {useCallback, useState} from "react";
import {Alert, Container} from "react-bootstrap";
import {useFetchAll} from "../../../utils/hook/useFetch";
import {AccessLogRepository, TileRepository} from "../../../repository";
import {Loading} from "../../../component/Loading/Loading";
import {MultiselectFilter} from "../../../component/filter/MultiselectFilter";
import {useKeycloak} from "@react-keycloak/web";
import {AccessLog, Tile} from "../../../model";
import {StatGraph} from "./Graph";
import {SelectFilter} from "../../../component/filter/SelectFilter";
import {DateFilter} from "../../../component/filter/DateFilter";

export const Statistics = () => {

    const { keycloak } = useKeycloak();

    const fetchFn = useCallback(() => {
        const repository = new AccessLogRepository(keycloak)
        return repository.readAll();
    }, [keycloak.token])
    const [loading, data, error] = useFetchAll(fetchFn)

    const fetchTilesFn = useCallback(() => {
        const repository = new TileRepository(keycloak)
        return repository.readAll()
    }, [keycloak.token])
    const [tileLoading, tileData, tileError] = useFetchAll(fetchTilesFn)

    const buildContent = () => {
        if (loading || tileLoading) {
            return <div className={`d-flex-center`}><Loading/></div>
        }
        if (error || tileError) {
            return <Alert variant={'danger'}>{error || tileError}</Alert>
        }

        const groupOptions = Array.from(new Set(
            data
                .flatMap(accessLog => accessLog.authGroups) // aplatit directement
        )).sort();

        const tileOptions = Array.from(new Set(
            tileData
                .flatMap(tile => ({ label: tile.name, value: `${tile.id}` })) // aplatit directement
        )).sort();

        return <DashBoard
            groupOptions={groupOptions}
            tileOptions={tileOptions}
            data={data}
            tiles={tileData}
        />
    }

    return (
        <Page title={`Statistiques`}>
            <Container>
                {buildContent()}
            </Container>
        </Page>
    )
}

type DashBoardProps = {
    groupOptions: string[]
    tileOptions: {label: string; value: string}[]
    data: AccessLog[],
    tiles: Tile[]
}

const DashBoard = (props: DashBoardProps) => {

    const [selectedGroups, setSelectedGroups] = useState<{ label: string; value: string }[]>([]);
    const [selectedTiles, setSelectedTiles] = useState<{ label: string; value: string }[]>([]);
    const [metric, setMetric] = useState<{ label: string; value: "date"|"group"|"tile" }>({label: "Date", value: "date"})
    const [startDate, setStartDate] = useState<Date|null>(new Date("2025-04-04"))
    const [endDate, setEndDate] = useState<Date|null>(new Date())

    const selectedTilesIds = selectedTiles.map(tile => tile.value);
    const selectedGroupValues = selectedGroups.map(g => g.value);

    const filteredData = props.data
        .filter(data =>
            selectedTilesIds.length === 0
            ||
            selectedTilesIds.includes(`${data.tileId}`))
        .filter(data =>
            selectedGroupValues.length === 0
            ||
            data.authGroups.some((groups: string) => selectedGroupValues.includes(groups))
        )
        .filter(data => {
            if (!startDate) {return true}
            const logDate = new Date(data.date)
            return logDate >= startDate
        })
        .filter(data => {
            if (!endDate) {return true}
            const logDate = new Date(data.date)
            return logDate <= endDate
        })
    ;

    return (
        <div className={`mt-2`}>
            <div className={`d-flex gap-2`}>
                <div className={'d-flex-center flex-grow-1 gap-1'}>
                    <div>{`Métrique`}</div>
                    <SelectFilter<"date"|"group"|"tile">
                        className={'flex-grow-1'}
                        label={`Groupes`}
                        value={metric}
                        options={[
                            {label: "Date", value: "date"},
                            {label: "Groupe", value: "group"},
                            {label: "Tuile", value: "tile"},
                        ]}
                        setValue={setMetric}
                    />
                </div>
                <div className={'d-flex-center flex-grow-1 gap-1'}>
                    <div>{`Groupes`}</div>
                    <MultiselectFilter
                        className={'flex-grow-1'}
                        label={`Groupes`}
                        value={selectedGroups}
                        options={props.groupOptions.map(option => ({ value: option, label: option }))}
                        setValue={setSelectedGroups}
                    />
                </div>
                <div className={'d-flex-center flex-grow-1 gap-1'}>
                    <div>{`Tuiles`}</div>
                    <MultiselectFilter
                        className={'flex-grow-1'}
                        label={`Tuiles`}
                        value={selectedTiles}
                        options={props.tileOptions}
                        setValue={setSelectedTiles}
                    />
                </div>
            </div>

            <div className={`d-flex gap-2`}>
                <div className={'d-flex-center flex-grow-1 gap-1'}>
                    <div>{`Date de début`}</div>
                    <DateFilter
                        className={'flex-grow-1'}
                        value={startDate}
                        options={props.tileOptions}
                        setValue={setStartDate}
                    />
                </div>
                <div className={'d-flex-center flex-grow-1 gap-1'}>
                    <div>{`Date de fin`}</div>
                    <DateFilter
                        className={'flex-grow-1'}
                        value={endDate}
                        options={props.tileOptions}
                        setValue={setEndDate}
                    />
                </div>
            </div>
            <div>
                <StatGraph
                    data={filteredData}
                    metric={metric.value}
                    tiles={props.tiles}
                />
            </div>
        </div>
    )
}